import {
  Component,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { BranchesService } from 'src/app/services/branches.service';
import { OperatingHoursService } from 'src/app/services/operating-hours.service';
import { GoogleMapsLoaderService } from 'src/app/services/google-maps-loader.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  buildGroupedScheduleLinesFromOperatingHours,
  formatGroupedScheduleHours,
  formatGroupedScheduleLineLabel,
  GroupedScheduleLineView,
} from 'src/app/shared/promotion-detail-modal/promotion-display.util';

interface City {
  name: string;
  count: number;
  branches: BranchData[];
}

interface BranchContact {
  type: string;
  value: string;
}

interface BranchData {
  id: number;
  branchName: string;
  name?: string;
  status: string;
  coreId: number;
  amenities?: string[];
  officeImageUrl?: string;
  address: {
    id: number;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  };
  contacts?: BranchContact[];
  phone?: string;
}

const CITY_ABBREVIATIONS: Record<string, string> = {
  'Ciudad de México': 'CDMX',
  Guadalajara: 'GDL',
  Monterrey: 'MTY',
};

@Component({
  selector: 'app-home-branches',
  templateUrl: './home-branches.component.html',
  styleUrls: ['./home-branches.component.scss'],
})
export class HomeBranchesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapDesktop') mapDesktopElement?: ElementRef;
  @ViewChild('mapMobile') mapMobileElement?: ElementRef;

  map?: google.maps.Map;
  marker?: google.maps.marker.AdvancedMarkerElement;

  selectedCityIndex = 0;
  selectedBranchIndex = 0;
  cities: City[] = [];
  scheduleLines: GroupedScheduleLineView[] = [];

  private subscription?: Subscription;
  private branchChangeSubscription?: Subscription;
  private scheduleSubscription?: Subscription;
  private langChangeSubscription?: Subscription;
  private initialLoadDone = false;
  private mapRequestId = 0;
  private readonly scheduleCache = new Map<number, GroupedScheduleLineView[]>();

  constructor(
    private readonly branchesService: BranchesService,
    private readonly operatingHoursService: OperatingHoursService,
    private readonly translate: TranslateService,
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.scheduleLines = [...this.scheduleLines];
    });

    this.loadBranches();

    this.branchChangeSubscription = this.branchesService.sucursalId.subscribe(
      (branchId: number) => {
        if (this.initialLoadDone && branchId > 0 && this.cities.length > 0) {
          this.selectBranchById(branchId);
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.branchChangeSubscription?.unsubscribe();
    this.scheduleSubscription?.unsubscribe();
    this.langChangeSubscription?.unsubscribe();
  }

  private loadBranches(): void {
    this.subscription = this.branchesService.getAll().subscribe({
      next: (branches: any[]) => {
        this.groupBranchesByCity(branches);

        this.branchesService.sucursalId.pipe(take(1)).subscribe((branchId) => {
          if (branchId > 0) {
            this.selectBranchById(branchId);
          } else if (
            this.cities.length > 0 &&
            this.cities[0].branches.length > 0
          ) {
            this.refreshSelectedBranchView();
          }
          this.initialLoadDone = true;
        });
      },
      error: (error) => {
        console.error('Error al cargar sucursales:', error);
      },
    });
  }

  private groupBranchesByCity(branches: BranchData[]): void {
    const citiesMap = new Map<string, BranchData[]>();

    branches.forEach((branch) => {
      const city = branch.address?.city || 'Sin ciudad';
      if (!citiesMap.has(city)) {
        citiesMap.set(city, []);
      }
      citiesMap.get(city)!.push(branch);
    });

    this.cities = Array.from(citiesMap.entries())
      .map(([city, cityBranches]) => ({
        name: city,
        count: cityBranches.length,
        branches: cityBranches,
      }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
      );
  }

  private selectBranchById(branchId: number): void {
    for (let cityIndex = 0; cityIndex < this.cities.length; cityIndex++) {
      const branchIndex = this.cities[cityIndex].branches.findIndex(
        (b) => b.id === branchId,
      );
      if (branchIndex !== -1) {
        this.selectedCityIndex = cityIndex;
        this.selectedBranchIndex = branchIndex;
        this.refreshSelectedBranchView();
        break;
      }
    }
  }

  private refreshSelectedBranchView(): void {
    const branch = this.selectedBranch;
    if (!branch) {
      return;
    }
    void this.setMap(branch);
    this.loadBranchSchedule(branch.id);
  }

  private loadBranchSchedule(branchId: number): void {
    const cached = this.scheduleCache.get(branchId);
    if (cached) {
      this.scheduleLines = cached;
      return;
    }

    this.scheduleSubscription?.unsubscribe();
    this.scheduleSubscription = this.operatingHoursService
      .getOperatingHours(branchId)
      .subscribe({
        next: (hours) => {
          const lines = buildGroupedScheduleLinesFromOperatingHours(hours);
          this.scheduleCache.set(branchId, lines);
          if (this.selectedBranch?.id === branchId) {
            this.scheduleLines = lines;
          }
        },
        error: (error) => {
          console.error('Error al cargar horarios de sucursal:', error);
          if (this.selectedBranch?.id === branchId) {
            this.scheduleLines = [];
          }
        },
      });
  }

  private getActiveMapElement(): HTMLElement | null {
    if (typeof window === 'undefined') {
      return this.mapDesktopElement?.nativeElement ?? null;
    }
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      return this.mapMobileElement?.nativeElement ?? null;
    }
    return this.mapDesktopElement?.nativeElement ?? null;
  }

  private async setMap(branch: BranchData): Promise<void> {
    if (!branch?.address) {
      return;
    }

    const requestId = ++this.mapRequestId;
    this.cdr.detectChanges();

    const mapContainer = this.getActiveMapElement();
    if (!mapContainer) {
      return;
    }

    try {
      if (this.marker) {
        this.marker.map = null;
        this.marker = undefined;
      }

      const { map, marker } = await this.googleMapsLoader.createBranchMap({
        element: mapContainer,
        latitude: Number(branch.address.latitude),
        longitude: Number(branch.address.longitude),
        title: branch.branchName || branch.name,
      });

      if (requestId !== this.mapRequestId) {
        marker.map = null;
        return;
      }

      this.map = map;
      this.marker = marker;
    } catch (error) {
      console.error('Error al inicializar el mapa de sucursales:', error);
    }
  }

  get selectedCity(): City | undefined {
    return this.cities[this.selectedCityIndex];
  }

  get selectedBranch(): BranchData | undefined {
    return this.selectedCity?.branches[this.selectedBranchIndex];
  }

  selectCity(index: number): void {
    if (this.selectedCityIndex !== index) {
      this.selectedCityIndex = index;
      this.selectedBranchIndex = 0;
      this.syncSelectedBranch();
    }
  }

  onCitySelectChange(index: number): void {
    this.selectCity(index);
  }

  selectBranch(index: number): void {
    this.selectedBranchIndex = index;
    this.syncSelectedBranch();
  }

  private syncSelectedBranch(): void {
    const branch = this.selectedBranch;
    if (!branch) {
      return;
    }

    this.refreshSelectedBranchView();
    this.branchesService.setBranchSelected({
      ...branch,
      name: branch.branchName || branch.name || '',
    } as any);
  }

  getCitySelectLabel(city: City): string {
    const abbrev = this.resolveCityAbbreviation(city.name);
    return abbrev ? `${abbrev} - ${city.name}` : city.name;
  }

  getCityShortLabel(city: City | undefined): string {
    if (!city) {
      return '';
    }
    return this.resolveCityAbbreviation(city.name) ?? city.name;
  }

  private resolveCityAbbreviation(cityName: string): string | undefined {
    const normalized = cityName.trim().toLowerCase();
    const entry = Object.entries(CITY_ABBREVIATIONS).find(
      ([key]) => key.toLowerCase() === normalized,
    );
    return entry?.[1];
  }

  hasAmenity(amenity: string): boolean {
    const amenities = this.selectedBranch?.amenities || [];
    return amenities.includes(amenity);
  }

  getBranchContact(type: string): string {
    const contacts = this.selectedBranch?.contacts || [];
    const contact = contacts.find((c: BranchContact) => c.type === type);
    return contact?.value || '';
  }

  getBranchPhone(): string {
    return this.getBranchContact('PHONE');
  }

  getBranchWhatsapp(): string {
    return this.getBranchContact('WHATSAPP');
  }

  getWhatsappUrl(): string {
    const phone = this.getBranchWhatsapp();
    return phone ? `https://wa.me/${phone}` : '';
  }

  getGoogleMapsUrl(): string {
    if (!this.selectedBranch?.address) {
      return '';
    }
    const lat = this.selectedBranch.address.latitude;
    const lng = this.selectedBranch.address.longitude;
    return `https://www.google.com/maps/search/?api=1&query=${lat}%2C${lng}`;
  }

  getFullAddress(): string {
    if (!this.selectedBranch?.address) {
      return '';
    }
    const addr = this.selectedBranch.address;
    return `${addr.street}, ${addr.city}, ${addr.state} ${addr.zipCode}`;
  }

  getBranchName(): string {
    return this.selectedBranch?.branchName || this.selectedBranch?.name || '';
  }

  getScheduleLineLabel(line: GroupedScheduleLineView): string {
    return formatGroupedScheduleLineLabel(
      line,
      this.translate.instant.bind(this.translate)
    );
  }

  getScheduleHours(line: GroupedScheduleLineView): string {
    return formatGroupedScheduleHours(
      line,
      this.translate.instant.bind(this.translate)
    );
  }

  triggerReserveDialog(): void {
    if (this.selectedBranch) {
      this.branchesService.setBranchSelected({
        ...this.selectedBranch,
        name: this.selectedBranch.branchName || this.selectedBranch.name || '',
      } as any);
    }
    this.branchesService.triggerReserveDialog();
  }
}
